import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Grid, Group, Paper, Stack, Tabs, Text, Title } from "@mantine/core";
import { FormErrors } from "@mantine/form/lib/types";
import { showNotification } from "@mantine/notifications";
import { debounce } from "lodash";
import { Client, CreateCampaignInquiryInput } from "src/graphql/generated/graphql";
import { DeepPartial } from "src/types/utils";

import { handleError } from "../../utils/error";
import {
    CreateCampaignInquiryAdditionalNotesForm,
    CreateCampaignInquiryDeliveryForm,
    CreateCampaignInquiryFormProvider,
    CreateCampaignInquiryGeneralForm,
    CreateCampaignInquiryPricingForm,
    CreateCampaignInquiryProductsForm,
    CreateCampaignInquiryQuestionnaireForm,
    CreateCampaignInquiryQuestionnaireFormRef,
    useCreateCampaignInquiryForm,
} from "../CreateCampaignInquiryForm";
import { CreateCampaignInquiryFormValues,createEanCheckState } from "../CreateCampaignInquiryForm/formContext";
import {
    CampaignInquiryDraftIntent,
    campaignInquirySectionLabelKeys,
    campaignInquirySectionTabIndex,
    clearCampaignInquiryDraft,
    getDefaultValues,
    getFailedCampaignInquirySections,
    getFormValues,
    getQuestionnaireLimit,
    hasQuestionnaireError,
    isOpenAtInPast,
    PartialCreateCampaignInquiryInput,
    readCampaignInquiryDraft,
    writeCampaignInquiryDraft,
} from "../CreateCampaignInquiryForm/utils";
import { CreateCampaignInquiryTab } from "./tab";

export interface CreateCampaignInquiryProps {
    client: Client;
    onCreate?: (campaignInquiry: CreateCampaignInquiryInput) => Promise<boolean | void> | boolean | void;
    initialValues?: DeepPartial<CreateCampaignInquiryFormValues>;
    /** Which piece of work this form represents — a draft is only ever restored onto the same one. */
    draftIntent?: CampaignInquiryDraftIntent;
}

/**
 * Is there anything here worth recovering? An untouched form is not a draft: the
 * products list and the questionnaire each auto-seed one empty entry on mount,
 * which is enough to make Mantine consider the form dirty about a second after
 * it opens. Persisting that produced a draft nobody had written (TTV2-1569).
 */
const hasAuthoredContent = (values?: PartialCreateCampaignInquiryInput) =>
    !!values?.name?.trim() ||
    !!values?.tenantId ||
    !!values?.description ||
    !!values?.thumbnailImage ||
    !!values?.additionalNotes?.trim() ||
    !!values?.delivery?.notes?.trim() ||
    (values?.products || []).some(
        (product) => !!product?.id || !!product?.ean || !!product?.name || !!product?.category?.key
    ) ||
    (values?.questionnaire?.questions || []).some((question) => !!question?.text?.trim());

export const CreateCampaignInquiry = ({
    client,
    onCreate,
    initialValues,
    draftIntent = "new",
}: CreateCampaignInquiryProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();

    const [activeIndex, setActiveIndex] = useState(0);

    // Shared verdict store for the EAN duplicate check. A ref because Mantine's
    // validate is synchronous while the check is an async fetch.
    const eanCheckRef = useRef(createEanCheckState());

    // R10b: the questionnaire's own fields are bound to this nested form, not to the
    // inquiry form's questionnaire.* errors (R10) — see the comment in questionnaire.tsx.
    const questionnaireFormRef = useRef<CreateCampaignInquiryQuestionnaireFormRef>();

    // `??` inside (see getQuestionnaireLimit): a client configured with a limit of 0 has to see 0
    // here, or the editor would offer questions the server then refuses to save.
    const questionsLimit = getQuestionnaireLimit(client);

    const createCampaignInquiryForm = useCreateCampaignInquiryForm(
        {
            // Always a clean sheet. A saved draft is offered below, never applied
            // behind the client's back.
            initialValues: initialValues || getDefaultValues(client),
        },
        eanCheckRef,
        questionsLimit
    );

    const errorsJson = JSON.stringify(createCampaignInquiryForm.errors);

    const [draftOffer, setDraftOffer] = useState<PartialCreateCampaignInquiryInput | null>(null);

    useEffect(() => {
        if (!client?.id) {
            return;
        }

        setDraftOffer(readCampaignInquiryDraft(client.id, draftIntent));
    }, [client?.id, draftIntent]);

    const handleResumeDraft = useCallback(() => {
        if (draftOffer) {
            // Route through the same normalisation a duplicate goes through (R13's
            // getFormValues) rather than applying the stored values verbatim — a draft
            // saved just before midnight and resumed after it must have its openAt
            // healed the same way, not carry a now-past date back into the form.
            //
            // That heal is silent by construction, and a start date the client picked
            // is not ours to change without a word: they would otherwise submit a
            // schedule they never chose. Say what happened before replacing it.
            if (isOpenAtInPast(draftOffer?.settings?.openAt)) {
                showNotification({
                    color: "yellow",
                    title: t("campaigninquiry_draft_openat_reset_title"),
                    message: t("campaigninquiry_draft_openat_reset_message"),
                });
            }

            createCampaignInquiryForm.setValues(getFormValues(draftOffer));
        }
        setDraftOffer(null);
        // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftOffer, t]);

    const handleDiscardDraft = useCallback(() => {
        clearCampaignInquiryDraft(client?.id, draftIntent);
        setDraftOffer(null);
    }, [client?.id, draftIntent]);

    useEffect(() => {
        if (!client?.id || !hasAuthoredContent(createCampaignInquiryForm.values)) {
            return;
        }
        const handleSaveDraft = debounce(() => {
            writeCampaignInquiryDraft(client.id, draftIntent, createCampaignInquiryForm.values);
        }, 1000);

        handleSaveDraft();

        return () => {
            handleSaveDraft.cancel();
        };
        // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(createCampaignInquiryForm.values), client?.id, draftIntent]);

    const handleClearForm = useCallback(() => {
        clearCampaignInquiryDraft(client?.id, draftIntent);
        eanCheckRef.current = createEanCheckState();
        createCampaignInquiryForm.reset();
        createCampaignInquiryForm.setValues(getDefaultValues(client));
        setDraftOffer(null);
        // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client, draftIntent]);

    const handleSubmit = useCallback(
        async (campaignInquiry: CreateCampaignInquiryInput) => {
            try {
                const result = await onCreate?.(campaignInquiry);
                // Only drop the draft if onCreate was successful
                if (result !== false) {
                    clearCampaignInquiryDraft(client?.id, draftIntent);
                }
            } catch (error) {
                handleError(error, {
                    message: { key: "campaigninquiry_saving_failed", namespace: "common" },
                });
            }
        },
        [client?.id, draftIntent, onCreate]
    );

    // TTV2-1690 — the submit button lives on the last tab only, so a rule that belongs
    // to an earlier one (R14's settings.openAt above all) refused the submit with no
    // notification, no tab switch and no visible marker anywhere near the client: they
    // clicked "Vytvořit poptávku" and nothing happened. That is the same silent refusal
    // this ticket removes from the admin transform screen, one SPA over. Mantine calls
    // this whenever validate() fails, so it is the one place a refusal cannot escape.
    const handleValidationFailure = useCallback(
        (errors: FormErrors) => {
            const failedSections = getFailedCampaignInquirySections(errors);

            if (failedSections.length === 0) {
                return;
            }

            showNotification({
                color: "red",
                title: t("campaigninquiry_create_refused_title"),
                message: t("campaigninquiry_create_refused_message", {
                    sections: failedSections.map((section) => t(campaignInquirySectionLabelKeys[section])).join(", "),
                }),
            });

            // Land on the first tab that refused, not the one they happened to be on.
            setActiveIndex(campaignInquirySectionTabIndex(failedSections[0]));
        },
        [t]
    );

    const handleNextStep = useCallback((event) => {
        event?.preventDefault?.();

        setActiveIndex((activeIndex) => activeIndex + 1);
    }, []);

    const handlePreviousStep = useCallback((event) => {
        event?.preventDefault?.();

        setActiveIndex((activeIndex) => activeIndex - 1);
    }, []);

    const tabs = [
        {
            title: t("campaigninquiry_create_tab_settings"),
            content: (
                <Stack>
                    <Paper p={36} radius={12} pos="relative">
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaign_editor_general")}
                        </Title>
                        <CreateCampaignInquiryGeneralForm />
                    </Paper>
                    <Paper p={36} radius={12}>
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaign_editor_pricing")}
                        </Title>
                        <CreateCampaignInquiryPricingForm />
                    </Paper>
                    <Paper p={36} radius={12}>
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaign_editor_delivery")}
                        </Title>
                        <CreateCampaignInquiryDeliveryForm />
                    </Paper>
                    <Paper p={36} radius={12}>
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaigninquiry_additionalnotes")}
                        </Title>
                        <CreateCampaignInquiryAdditionalNotesForm />
                    </Paper>
                </Stack>
            ),
            hasError: useCallback(() => {
                // Ownership by exclusion rather than a hand-maintained field list: the
                // Settings tab owns every error key the other two tabs don't claim
                // (products.* and questionnaire.*), so a new top-level rule — like R14's
                // settings.openAt — marks this tab without needing a matching edit here.
                for (const field of Object.keys(createCampaignInquiryForm.errors)) {
                    if (
                        !!createCampaignInquiryForm.errors[field] &&
                        !/^products\./.test(field) &&
                        !/^questionnaire\./.test(field)
                    ) {
                        return true;
                    }
                }

                return false;
                // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errorsJson]),
        },
        {
            title: t("campaigninquiry_create_tab_products"),
            content: <CreateCampaignInquiryProductsForm eanCheckRef={eanCheckRef} />,
            hasError: useCallback(() => {
                for (const field of Object.keys(createCampaignInquiryForm.errors)) {
                    if (/^products\./.test(field) && !!createCampaignInquiryForm.errors[field]) {
                        return true;
                    }
                }

                return false;
                // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errorsJson]),
        },
        {
            title: t("campaigninquiry_create_tab_questionare"),
            content: (
                <CreateCampaignInquiryQuestionnaireForm
                    ref={questionnaireFormRef}
                    questionsLimit={questionsLimit}
                />
            ),
            hasError: useCallback(() => {
                return hasQuestionnaireError(createCampaignInquiryForm.errors);
                // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errorsJson]),
        },
    ];

    return (
        <Tabs
            styles={{
                tabsList: {
                    display: "flex",
                    flexDirection: "column",
                },
                tab: {
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 4,
                    gap: 20,
                    "&[data-active] .mantine-Tabs-tabLabel": {
                        background: "#EEEFF2",
                    },
                },
                tabLabel: {
                    paddingBlock: 8,
                    paddingInline: 12,
                    borderRadius: 8,
                },
                tabIcon: {
                    height: 12,
                    width: 12,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                },
            }}
            value={String(activeIndex)}
            onTabChange={(index) => setActiveIndex(Number(index))}
            unstyled
        >
            <Grid>
                <Grid.Col pr={30} lg="content">
                    <Tabs.List>
                        {tabs.map((tab, index) => (
                            <CreateCampaignInquiryTab
                                key={index}
                                value={String(index)}
                                isActive={index === activeIndex}
                                isCompleted={activeIndex > index}
                                isLast={index >= tabs.length - 1}
                                hasError={tab.hasError()}
                            >
                                {tab.title}
                            </CreateCampaignInquiryTab>
                        ))}
                    </Tabs.List>
                </Grid.Col>
                <Grid.Col lg="auto">
                    {!!draftOffer && (
                        <Alert
                            color="blue"
                            mb={24}
                            title={t("campaigninquiry_draft_title")}
                            withCloseButton={false}
                        >
                            <Stack align="flex-start" spacing="sm">
                                <Text size="sm">{t("campaigninquiry_draft_text")}</Text>
                                <Group spacing="sm">
                                    <Button variant="light" size="sm" onClick={handleResumeDraft}>
                                        {t("campaigninquiry_draft_resume")}
                                    </Button>
                                    <Button variant="subtle" size="sm" onClick={handleDiscardDraft}>
                                        {t("campaigninquiry_draft_discard")}
                                    </Button>
                                </Group>
                            </Stack>
                        </Alert>
                    )}
                    <CreateCampaignInquiryFormProvider form={createCampaignInquiryForm}>
                        <Box
                            component="form"
                            style={{ marginBottom: "4rem" }}
                            onSubmit={(event) => {
                                // R10b: also validate the nested questionnaire form, so an
                                // invalid question/option is marked on the field itself and
                                // not just on the Dotazník tab (R11).
                                questionnaireFormRef.current?.validate?.();

                                createCampaignInquiryForm.onSubmit(handleSubmit, handleValidationFailure)(event);
                            }}
                            onKeyDown={(event) => {
                                if (event?.key === "Enter") {
                                    event.preventDefault?.();
                                }
                            }}
                        >
                            {tabs.map(({ content }, index) => (
                                <Tabs.Panel key={index} value={String(index)}>
                                    <Paper mb={24} p={24} radius={12} withBorder sx={{ backgroundColor: "white" }}>
                                        <Title order={4} mb={16}>
                                            {t("inquiry_guide_title")}
                                        </Title>
                                        <Group spacing="md">
                                            <Button
                                                component="a"
                                                href="https://www.loom.com/share/49baa6a9ce2e480fb2fabf37c4fdda76"
                                                target="_blank"
                                                variant="outline"
                                                leftIcon={<span>📹</span>}
                                            >
                                                {t("video_tutorial_part1")}
                                            </Button>
                                            <Button
                                                component="a"
                                                href="https://www.loom.com/share/6b8def1d552c490685083fc12f555f03"
                                                target="_blank"
                                                variant="outline"
                                                leftIcon={<span>📹</span>}
                                            >
                                                {t("video_tutorial_part2")}
                                            </Button>
                                        </Group>
                                    </Paper>
                                    {content}
                                    <Group position="apart" mt={24}>
                                        {index > 0 ? (
                                            <Button
                                                variant="default"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                                onClick={handlePreviousStep}
                                            >
                                                {t("previousstep")}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="default"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                                onClick={() => navigate("/campaigns/inquiries")}
                                            >
                                                {t("cancel")}
                                            </Button>
                                        )}
                                        <Button
                                            variant="default"
                                            size="lg"
                                            color="primary.main"
                                            radius="md"
                                            style={{
                                                fontSize: 14,
                                            }}
                                            onClick={handleClearForm}
                                        >
                                            {t("campaigninquiry_clear")}
                                        </Button>
                                        {index < tabs.length - 1 ? (
                                            <Button
                                                variant="filled"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                                onClick={handleNextStep}
                                            >
                                                {t("nextstep")}
                                            </Button>
                                        ) : (
                                            <Button
                                                type="submit"
                                                variant="filled"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                            >
                                                {t("campaigninquiry_create")}
                                            </Button>
                                        )}
                                    </Group>
                                </Tabs.Panel>
                            ))}
                        </Box>
                    </CreateCampaignInquiryFormProvider>
                </Grid.Col>
            </Grid>
        </Tabs>
    );
};
